/**
 * PatrolInterruptionModal - Component to interrupt an active patrol
 * 
 * PHASE 2: Allows officers to interrupt a patrol due to operational events.
 * Requires a reason (min 10 chars) and optionally links to an incident.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Loader2,
  AlertCircle,
  Link as LinkIcon,
  FileText
} from 'lucide-react';

const COMMON_REASONS = [
  "Emergency response required",
  "Incident requiring immediate attention",
  "Weather conditions unsafe",
  "Equipment failure",
  "Client request",
  "Medical emergency",
  "Security threat",
  "Vehicle breakdown",
  "Other (specify below)"
];

export default function PatrolInterruptionModal({ 
  open, 
  onOpenChange, 
  shiftId, 
  onInterrupted 
}) {
  const { api } = useAuth();
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [incidentId, setIncidentId] = useState('');
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingIncidents, setLoadingIncidents] = useState(false);

  // Fetch recent incidents for linking
  useEffect(() => {
    const fetchIncidents = async () => {
      if (!open) return;
      
      setLoadingIncidents(true);
      try {
        const res = await api.get('/incidents?limit=20&status=DRAFT,SUBMITTED');
        setIncidents(res.data.items || res.data || []);
      } catch (err) {
        console.error('Failed to fetch incidents:', err);
      } finally {
        setLoadingIncidents(false);
      }
    };
    
    fetchIncidents();
  }, [api, open]);

  // Get the final reason text
  const getFinalReason = () => {
    if (reason === 'Other (specify below)' || reason === '') {
      return customReason;
    }
    return reason;
  };

  const isValidReason = getFinalReason().trim().length >= 10;

  // Handle interruption
  const handleInterrupt = async () => {
    const finalReason = getFinalReason();
    
    if (finalReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    
    setLoading(true);
    try {
      // Get GPS if available
      let latitude = null;
      let longitude = null;
      
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            });
          });
          latitude = pos.coords.latitude;
          longitude = pos.coords.longitude;
        } catch (gpsErr) {
          console.warn('GPS unavailable:', gpsErr);
        }
      }
      
      const res = await api.post(`/shifts/${shiftId}/patrol/interruption`, {
        reason: finalReason,
        incident_id: incidentId || null,
        notes: notes || null,
        latitude,
        longitude,
        device_id: navigator.userAgent.substring(0, 50)
      });
      
      toast.success('Patrol interrupted');
      onInterrupted?.(res.data);
      onOpenChange(false);
      
      // Reset form
      setReason('');
      setCustomReason('');
      setNotes('');
      setIncidentId('');
      
    } catch (err) {
      console.error('Failed to interrupt patrol:', err);
      toast.error(err.response?.data?.detail || 'Failed to interrupt patrol');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Interrupt Patrol
          </DialogTitle>
          <DialogDescription>
            Record the reason for interrupting this patrol. This will be logged for audit purposes and allow you to sign off.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Common Reasons */}
          <div>
            <Label className="text-zinc-400">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {COMMON_REASONS.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Custom Reason / Details */}
          <div>
            <Label className="text-zinc-400">
              {reason === 'Other (specify below)' ? 'Specify Reason *' : 'Additional Details'}
            </Label>
            <Textarea
              value={reason === 'Other (specify below)' ? customReason : notes}
              onChange={(e) => {
                if (reason === 'Other (specify below)') {
                  setCustomReason(e.target.value);
                } else {
                  setNotes(e.target.value);
                }
              }}
              placeholder={reason === 'Other (specify below)' 
                ? "Enter the reason for interruption (min 10 characters)..."
                : "Optional additional notes..."
              }
              rows={3}
              className="mt-1"
            />
            {reason === 'Other (specify below)' && customReason.length > 0 && customReason.length < 10 && (
              <p className="text-xs text-amber-500 mt-1">
                {10 - customReason.length} more characters required
              </p>
            )}
          </div>
          
          {/* Link Incident */}
          <div>
            <Label className="text-zinc-400 flex items-center gap-1">
              <LinkIcon className="w-3 h-3" />
              Link to Incident (Optional)
            </Label>
            <Select value={incidentId} onValueChange={setIncidentId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select an incident..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">None</SelectItem>
                {loadingIncidents ? (
                  <SelectItem value="" disabled>Loading...</SelectItem>
                ) : (
                  incidents.map(inc => (
                    <SelectItem key={inc.id} value={inc.id}>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            inc.severity === 'critical' ? 'border-red-500 text-red-400' :
                            inc.severity === 'high' ? 'border-amber-500 text-amber-400' :
                            'border-zinc-600 text-zinc-400'
                          }`}
                        >
                          {inc.severity}
                        </Badge>
                        <span className="truncate">{inc.title}</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          {/* Warning */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200">
              <p className="font-medium">Interruption Notice</p>
              <p className="text-xs text-amber-300/80 mt-1">
                This action will be recorded in the audit trail. You will be able to sign off after interrupting the patrol.
              </p>
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleInterrupt}
            disabled={loading || !isValidReason}
            data-testid="confirm-interrupt-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4 mr-2" />
            )}
            Interrupt Patrol
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
